import React, { useState, useEffect, useRef, useCallback } from "react";
import "./EmpHome.css";
import PlansSection from "../Plans/Plans";
import { Link, useNavigate } from "react-router-dom";

const API_ROOT = (process.env.REACT_APP_API_URL || "http://localhost:3000")
  .replace(/\/$/, "")
  .replace(/\/api$/, "");
const API_BASE = `${API_ROOT}/api`;

const HR_CALCULATORS = [
  { icon: "💰", label: "Salary Calculator",     path: "/employer/salary-calculator" },
  { icon: "📅", label: "HRA Calculator",        path: "/employer/hra-calculator" },
  { icon: "🏛️", label: "PF Calculator",         path: "/employer/pf-calculator" },
  { icon: "📊", label: "Tax Calculator",        path: "/employer/tax-calculator" },
  { icon: "⏱️", label: "PPF Calculator",        path: "/employer/ppf-calculator" },
  { icon: "🎯", label: "NPS Calculator",        path: "/employer/nps-calculator" },
  { icon: "📈", label: "Retirement Calculator", path: "/employer/retirement-calculator" },
  { icon: "📈", label: "Salary Guide",          path: "/employer/salary-guide" },
];

/* ═══════════════════════════════════════════════════════════
   SCROLL REVEAL HOOK
   Uses IntersectionObserver — adds .in-view to elements
   with class .reveal when they enter viewport.
   direction: "from-bottom" | "from-left" | "from-right" | "from-top" | "zoom" | "flip"
═══════════════════════════════════════════════════════════ */
function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    if (!els.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
          } else {
            // 🔥 REMOVE class when out of view
            entry.target.classList.remove("in-view");
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: "0px 0px -20px 0px",
      }
    );

    els.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);
}

/* Re-run reveal whenever component re-renders (new elements) */
function useRevealAll() {
  useEffect(() => {
    const run = () => {
      const els = document.querySelectorAll(".reveal:not(.in-view)");
      if (!els.length) return;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("in-view");
              // observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.10, rootMargin: "0px 0px -30px 0px" }
      );
      els.forEach((el) => observer.observe(el));
      return () => observer.disconnect();
    };
    const cleanup = run();
    return cleanup;
  });
}

/* ═══════════════════════════════════════════════════════════
   EYE ICON
═══════════════════════════════════════════════════════════ */
function EyeIcon({ open }) {
  return open ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════
   PASSWORD STRENGTH
═══════════════════════════════════════════════════════════ */
function PasswordStrength({ password }) {
  if (!password) return null;
  const score = [
    password.length >= 8,
    /[A-Z]/.test(password),
    /\d/.test(password),
    /[!@#$%^&*]/.test(password),
  ].filter(Boolean).length;
  const meta = [
    { label: "Weak",   color: "#394420" },
    { label: "Fair",   color: "#1d240e" },
    { label: "Good",   color: "#eab308" },
    { label: "Strong", color: "#263012" },
  ];
  const { label, color } = meta[score - 1] || meta[0];
  return (
    <div className="pwd-row">
      <div className="pwd-bars">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className={`pwd-bar ${i <= score ? "active" : ""}`}
            style={{ background: i <= score ? color : "#e2e8f0" }} />
        ))}
      </div>
      <span className="pwd-label" style={{ color }}>{score > 0 ? label : ""}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   VALIDATORS
═══════════════════════════════════════════════════════════ */
const VALIDATORS = {
  name:     v => !v?.trim() ? "Name is required" : v.trim().length < 2 ? "Min 2 characters" : null,
  email:    v => !v?.trim() ? "Email is required" : !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v) ? "Invalid email" : null,
  phone:    v => !v?.trim() ? "Phone is required" : !/^[\+]?[\d\s\-\(\)]{7,15}$/.test(v.replace(/\s/g, "")) ? "Invalid phone number" : null,
  password: v => !v ? "Password is required" : v.length < 8 ? "Min 8 characters" :
    !/[A-Z]/.test(v) ? "Add an uppercase letter" :
    !/\d/.test(v)    ? "Add a number" :
    !/[!@#$%^&*]/.test(v) ? "Add a special character" : null,
};

function splitFullName(fullName) {
  const parts = fullName.trim().split(/\s+/);
  return { firstName: parts[0] || "", lastName: parts.length > 1 ? parts.slice(1).join(" ") : "" };
}

/* ═══════════════════════════════════════════════════════════
   SECTION HEADING with animated underline
═══════════════════════════════════════════════════════════ */
function SectionHeading({ children, className = "" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className={`section-heading-wrap ${visible ? "section-visible" : ""} ${className}`}>
      {children}
      <div style={{
        height: 3, borderRadius: 99,
        background: "linear-gradient(135deg,#263012,#1d240e)",
        margin: "10px auto 0",
        width: visible ? 64 : 0,
        transition: "width 0.8s cubic-bezier(0.4,0,0.2,1) 0.3s",
      }} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
export default function EmployerHome() {
  const navigate   = useNavigate();
  const pricingRef = useRef(null);

  // Activate scroll reveal on every render
useScrollReveal();

  /* Sticky calc bar */
  const [stickyCalc, setStickyCalc] = useState(false);
  useEffect(() => {
    const fn = () => setStickyCalc(window.scrollY > 80);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  /* Form state */
  const [form,        setForm]        = useState({ name: "", email: "", phone: "", password: "" });
  const [errors,      setErrors]      = useState({});
  const [touched,     setTouched]     = useState({});
  const [showPwd,     setShowPwd]     = useState(false);
  const [success,     setSuccess]     = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [phoneExists, setPhoneExists] = useState(false);

  const validate = (name, value) => VALIDATORS[name]?.(value) ?? null;

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (touched[name]) setErrors(er => ({ ...er, [name]: validate(name, value) }));
  };

  useEffect(() => {
    if (!form.email || validate("email", form.email)) { setEmailExists(false); return; }
    const t = setTimeout(async () => {
      try {
        const res  = await fetch(`${API_BASE}/employer/check-email?email=${encodeURIComponent(form.email)}`);
        const data = await res.json();
        setEmailExists(data.exists);
      } catch {}
    }, 600);
    return () => clearTimeout(t);
  }, [form.email]);

  useEffect(() => {
    if (!form.phone || validate("phone", form.phone)) { setPhoneExists(false); return; }
    const t = setTimeout(async () => {
      try {
        const res  = await fetch(`${API_BASE}/employer/check-phone?phone=${encodeURIComponent(form.phone)}`);
        const data = await res.json();
        setPhoneExists(data.exists);
      } catch {}
    }, 600);
    return () => clearTimeout(t);
  }, [form.phone]);

  const scrollToPricing = () => pricingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const handleBlur = e => {
    const { name, value } = e.target;
    setTouched(t => ({ ...t, [name]: true }));
    setErrors(er => ({ ...er, [name]: validate(name, value) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {}, newTouched = {};
    let hasError = false;
    Object.keys(VALIDATORS).forEach(k => {
      newTouched[k] = true;
      const err = validate(k, form[k]);
      if (err) { newErrors[k] = err; hasError = true; }
    });
    setTouched(newTouched); setErrors(newErrors);
    if (hasError) return;
    if (emailExists) { setErrors(e => ({ ...e, email: "This email is already registered" })); return; }
    if (phoneExists) { setErrors(e => ({ ...e, phone: "This phone number is already registered" })); return; }
    const { firstName, lastName } = splitFullName(form.name);
    try {
      const res = await fetch(`${API_BASE}/employer/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ firstName, lastName, email: form.email, phone: form.phone, password: form.password }),
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem("token", data.token || "");
        localStorage.setItem("user", JSON.stringify({
          id: data.user?.id,
          firstName: data.user?.firstName ?? firstName,
          lastName:  data.user?.lastName  ?? lastName,
          email:     data.user?.email     ?? form.email,
          role: "employer",
        }));
        setSuccess(true);
        setForm({ name: "", email: "", phone: "", password: "" });
        setTimeout(() => navigate("/employer/profile"), 1200);
      } else {
        alert(data.message);
      }
    } catch (err) { console.log(err); }
  };

  const inpClass = name => [
    "sf-inp",
    touched[name] && errors[name]               ? "sf-err" : "",
    touched[name] && !errors[name] && form[name] ? "sf-ok"  : "",
  ].join(" ").trim();

  const Err = ({ name }) =>
    touched[name] && errors[name]
      ? <span className="sf-ferr">⚠ {errors[name]}</span>
      : null;

  return (
    <div className="employer-home">

      {/* SUCCESS POPUP */}
      {success && (
        <div className="signup-success-popup">
          🎉 Signup Successful! Welcome to Talent Hive 🚀
        </div>
      )}

      {/* ══════════════════════════════════════════
          HERO
      ══════════════════════════════════════════ */}
      <section className="employer-hero">
        <div className="bg-shape shape1" />
        <div className="bg-shape shape2" />

        <div className="hero-split">

          {/* LEFT — Signup Form (slides in from left on page load) */}
          <div className="hero-left">
            <div className="sf-card">
              <div className="sf-card-accent" />

              <div className="sf-head">
                <h2 className="sf-title">Get Started <span>Free</span></h2>
                <p className="sf-sub">Join thousands of companies hiring smarter on Talent Hive.</p>
              </div>

              <form className="sf-form" onSubmit={handleSubmit} noValidate>

                <div className="sf-field">
                  <label className="sf-label">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
                    </svg>
                    Full Name
                  </label>
                  <input className={inpClass("name")} name="name" type="text" placeholder="John Smith"
                    value={form.name} onChange={handleChange} onBlur={handleBlur} />
                  <Err name="name" />
                </div>

                <div className="sf-field">
                  <label className="sf-label">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
                    </svg>
                    Work Email
                  </label>
                  <input className={inpClass("email")} name="email" type="email" placeholder="you@company.com"
                    value={form.email} onChange={handleChange} onBlur={handleBlur} />
                  <Err name="email" />
                  {emailExists && <span className="sf-ferr">⚠ Email already registered</span>}
                </div>

                <div className="sf-field">
                  <label className="sf-label">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.72A2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z" />
                    </svg>
                    Phone Number
                  </label>
                  <input className={inpClass("phone")} name="phone" type="tel" placeholder="+91 98765 43210"
                    value={form.phone} onChange={handleChange} onBlur={handleBlur} />
                  <Err name="phone" />
                  {phoneExists && <span className="sf-ferr">⚠ Phone number already registered</span>}
                </div>

                <div className="sf-field">
                  <label className="sf-label">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
                    </svg>
                    Password
                  </label>
                  <div className="sf-pwd-wrap">
                    <input className={inpClass("password")} name="password"
                      type={showPwd ? "text" : "password"}
                      placeholder="Min 8 chars, uppercase & number"
                      value={form.password} onChange={handleChange} onBlur={handleBlur}
                      autoComplete="new-password" style={{ paddingRight: 42 }} />
                    <button type="button" className="sf-eye" onClick={() => setShowPwd(p => !p)} tabIndex={-1}>
                      <EyeIcon open={showPwd} />
                    </button>
                  </div>
                  <PasswordStrength password={form.password} />
                  <Err name="password" />
                </div>

                <button type="submit" className="sf-submit">
                  Sign Up
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                  </svg>
                </button>

                <div className="sf-divider"><span>or continue with</span></div>

                <div className="sf-socials">
                  <button type="button" className="sf-social-btn">
                    <svg width="18" height="18" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Google
                  </button>
                  <button type="button" className="sf-social-btn">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#0077B5">
                      <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" />
                      <circle cx="4" cy="4" r="2" fill="#0077B5" />
                    </svg>
                    LinkedIn
                  </button>
                </div>
              </form>

              <p className="sf-login-link">
                Already have an account? <a href="/employer/login">Sign in</a>
              </p>
            </div>
          </div>

          {/* RIGHT — Calculator Card (slides in from right on page load) */}
          <div className="hero-right">
            <div className="calc-header">
              <h2>HR <span>Power Tools</span></h2>
              <p>Quick-access calculators to supercharge your HR workflow</p>
            </div>
            <div className="calc-grid">
              {HR_CALCULATORS.map((c, i) => (
                <button
                  key={c.label}
                  className="calc-card-btn"
                  style={{ animationDelay: `${0.05 + i * 0.06}s` }}
                  onClick={() => navigate(c.path)}
                >
                  <span className="calc-icon">{c.icon}</span>
                  <span className="calc-label">{c.label}</span>
                  <span className="calc-arrow">→</span>
                </button>
              ))}
            </div>
            <div className="calc-footer-note">✨ All tools are free — only sign-up required</div>
          </div>

        </div>
      </section>

      {/* ══════════════════════════════════════════
          HOW IT WORKS
      ══════════════════════════════════════════ */}
      <section className="how-section">
        <div className="how-container">

          {/* Heading — zooms in from center */}
          <div className="reveal zoom" style={{ marginBottom: 14 }}>
            <SectionHeading>
              <h2>Simple. Efficient. <span>Effective Hiring.</span></h2>
            </SectionHeading>
          </div>

          <p className="how-subtitle reveal from-bottom d1">
            Talent Hive streamlines your recruitment journey in three simple steps —
            helping you hire faster and smarter.
          </p>

          <div className="steps">
            {/* Step 1 — from left */}
            <div className="step reveal from-left d1">
              <div className="step-number">01</div>
              <h4>Create Your Employer Profile</h4>
              <p>Register your company and set up your hiring preferences in minutes.</p>
            </div>

            {/* Step 2 — from bottom (center card pops up) */}
            <div className="step reveal from-bottom d3">
              <div className="step-number">02</div>
              <h4>Post Job Requirements</h4>
              <p>Share your job details and let our smart system connect you with relevant, verified candidates.</p>
            </div>

            {/* Step 3 — from right */}
            <div className="step reveal from-right d5">
              <div className="step-number">03</div>
              <h4>Review &amp; Hire</h4>
              <p>Shortlist top talent, connect instantly, and onboard the best fit without delays.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          PLANS
      ══════════════════════════════════════════ */}
      <div ref={pricingRef} className="reveal zoom d1">
        <PlansSection />
      </div>

      {/* ══════════════════════════════════════════
          WHY SECTION
      ══════════════════════════════════════════ */}
      <section className="why-section">
        <div className="why-container">

          {/* Heading — flips in */}
          <div className="reveal flip" style={{ marginBottom: 14 }}>
            <SectionHeading>
              <h2>Why Employers Trust <span>Talent Hive</span></h2>
            </SectionHeading>
          </div>

          <p className="why-subtitle reveal from-bottom d2">
            We simplify hiring with verified talent, intelligent matching,
            and faster recruitment cycles — so you can focus on growing your business.
          </p>

          <div className="why-grid">
            {/* Card 1 — from left */}
            <div className="why-card reveal from-left d2">
              <div className="why-icon">✔</div>
              <h4>Verified &amp; Pre-Screened Talent</h4>
              <p>Every candidate profile is skill-verified and quality-checked.</p>
            </div>

            {/* Card 2 — zoom from center */}
            <div className="why-card reveal zoom d4">
              <div className="why-icon">🧠</div>
              <h4>Intelligent Talent Matching</h4>
              <p>AI-driven recommendations match you with the most relevant candidates.</p>
            </div>

            {/* Card 3 — from right */}
            <div className="why-card reveal from-right d6">
              <div className="why-icon">⚡</div>
              <h4>Accelerated Hiring Process</h4>
              <p>Reduce time-to-hire with streamlined communication and quick shortlisting.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          CTA
      ══════════════════════════════════════════ */}
      <section className="cta-section">
        <div className="cta-box reveal zoom d2">
          <SectionHeading>
            <h2>Start Hiring <span>Smarter &amp; Faster</span></h2>
          </SectionHeading>
          <p>
            Post jobs, access verified candidates, and grow your team with
            TalentHive's powerful hiring solutions.
          </p>
          <div className="cta-actions">
            <Link to="/employer/signup" className="primary-btn large">Get Started</Link>
            <button className="secondary-btn" onClick={scrollToPricing}>View Pricing</button>
          </div>
        </div>
      </section>

    </div>
  );
}